import React from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Calendar, Mail, User, UserCheck } from 'lucide-react';

const RegistrationSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const registrationType = searchParams.get('type') || 'reviewee';
  const eventId = searchParams.get('event') || '';

  const isReviewer = registrationType === 'reviewer';

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          {/* Success Icon */}
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>

          {/* Success Message */}
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Registration Submitted Successfully!
          </h1>
          
          <p className="text-lg text-gray-600 mb-8">
            Thank you for registering as a{' '}
            <span className="font-semibold text-primary-600">
              {isReviewer ? 'reviewer' : 'reviewee'}
            </span>
            {' '}for our portfolio review event.
          </p>

          {/* Next Steps */}
          <div className="bg-blue-50 rounded-lg p-6 mb-8 text-left">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Calendar className="h-5 w-5 mr-2 text-blue-600" />
              What happens next?
            </h2>
            
            {isReviewer ? (
              <div className="space-y-3 text-gray-700">
                <div className="flex items-start">
                  <Mail className="h-5 w-5 mr-3 mt-0.5 text-blue-600" />
                  <div>
                    <p className="font-medium">Email Confirmation</p>
                    <p className="text-sm">You'll receive a confirmation email within 24 hours with your reviewer guidelines and next steps.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <UserCheck className="h-5 w-5 mr-3 mt-0.5 text-blue-600" />
                  <div>
                    <p className="font-medium">Application Review</p>
                    <p className="text-sm">Our team will review your application and background to ensure a good match with our reviewee pool.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <Calendar className="h-5 w-5 mr-3 mt-0.5 text-blue-600" />
                  <div>
                    <p className="font-medium">Schedule Coordination</p>
                    <p className="text-sm">If accepted, we'll reach out to coordinate your availability and match you with reviewees based on your expertise.</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-3 text-gray-700">
                <div className="flex items-start">
                  <Mail className="h-5 w-5 mr-3 mt-0.5 text-blue-600" />
                  <div>
                    <p className="font-medium">Email Confirmation</p>
                    <p className="text-sm">You'll receive a confirmation email within 24 hours with your submission details.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <UserCheck className="h-5 w-5 mr-3 mt-0.5 text-blue-600" />
                  <div>
                    <p className="font-medium">Reviewer Matching</p>
                    <p className="text-sm">We'll match you with experienced professionals based on your goals and portfolio focus.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <Calendar className="h-5 w-5 mr-3 mt-0.5 text-blue-600" />
                  <div>
                    <p className="font-medium">Schedule Your Review</p>
                    <p className="text-sm">You'll receive an email with available time slots to book your review session within 1-2 weeks.</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Important Information */}
          <div className="bg-yellow-50 rounded-lg p-6 mb-8 text-left">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Important Information</h3>
            <div className="space-y-2 text-sm text-gray-700">
              {isReviewer ? (
                <>
                  <p>• Review our volunteer reviewer guidelines that will be sent to your email</p>
                  <p>• Ensure your calendar is up to date for scheduling coordination</p>
                  <p>• Prepare any materials or examples you'd like to reference during reviews</p>
                  <p>• Questions? Contact us at reviewers@reviewplatform.com</p>
                </>
              ) : (
                <>
                  <p>• Keep your portfolio materials ready and accessible for review</p>
                  <p>• Prepare specific questions or areas you'd like feedback on</p>
                  <p>• Ensure you have a stable internet connection for video reviews</p>
                  <p>• Questions? Contact us at support@reviewplatform.com</p>
                </>
              )}
            </div>
          </div>

          {/* Registration Details */}
          <div className="bg-gray-50 rounded-lg p-6 mb-8 text-left">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Registration Details</h3>
            <div className="space-y-2 text-sm text-gray-600">
              <p><span className="font-medium">Registration Type:</span> {isReviewer ? 'Volunteer Reviewer' : 'Portfolio Reviewee'}</p>
              <p><span className="font-medium">Event ID:</span> {eventId}</p>
              <p><span className="font-medium">Submitted:</span> {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}</p>
              <p><span className="font-medium">Status:</span> <span className="text-blue-600 font-medium">Pending Review</span></p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/"
              className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Return to Home
            </Link>
            
            {!isReviewer && (
              <Link
                to={`/register/reviewer/${eventId}`}
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <User className="h-5 w-5 mr-2" />
                Register as Reviewer
              </Link>
            )}
          </div>

          {/* Contact Information */}
          <div className="mt-8 pt-8 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              Need help? Contact us at{' '}
              <a 
                href={`mailto:${isReviewer ? 'reviewers' : 'support'}@reviewplatform.com`}
                className="text-primary-600 hover:text-primary-500"
              >
                {isReviewer ? 'reviewers' : 'support'}@reviewplatform.com
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrationSuccess;